import { useEffect, useState } from 'react'
import { Link } from 'wouter'
import { authedFetch } from './authedFetch'

export default function AdminFeeds() {
  const [feeds, setFeeds] = useState<AdminFeed[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadFeeds = async () => {
      try {
        const res = await authedFetch('/api/admin/feeds')
        if (res.ok) {
          setFeeds(await res.json())
        }
      } finally {
        setLoading(false)
      }
    }
    loadFeeds()
  }, [])

  const intervalOptions = [
    { value: 300, label: '5m (min)' },
    { value: 900, label: '15m' },
    { value: 3600, label: '1h' },
    { value: 14400, label: '4h' },
    { value: 43200, label: '12h' },
    { value: 86400, label: '1d' },
    { value: 86400 * 7, label: '7d' },
    { value: 86400 * 14, label: '14d' },
    { value: 86400 * 30, label: '30d (max)' },
  ]

  const toggleActive = async (feed: AdminFeed) => {
    const newActive = !feed.active
    setFeeds(prev => prev.map(f =>
      f.id === feed.id ? { ...f, active: newActive, updateFailures: newActive ? 0 : f.updateFailures } : f
    ))

    await authedFetch('/api/admin/feeds/update', {
      method: 'POST',
      body: new URLSearchParams({
        id: String(feed.id),
        active: String(newActive),
      }),
    })
  }

  const setFetchInterval = (feed: AdminFeed, seconds: number) => {
    setFeeds(prev => prev.map(f =>
      f.id === feed.id ? { ...f, fetchIntervalSeconds: seconds } : f
    ))
  }

  const saveFetchInterval = async (feed: AdminFeed) => {
    await authedFetch('/api/admin/feeds/update', {
      method: 'POST',
      body: new URLSearchParams({
        id: String(feed.id),
        fetchIntervalSeconds: String(feed.fetchIntervalSeconds),
      }),
    })
  }

  const formatTime = (timestamp: number | null): string => {
    if (!timestamp) {
      return 'Never'
    }
    return new Date(timestamp * 1000).toLocaleString()
  }

  if (loading) {
    return <div className="container">Loading...</div>
  }

  return (
    <div className="container">
      <h1>Admin: Feeds</h1>
      <p><Link href="/">Back to reader</Link></p>
      <table className="table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Status</th>
            <th>Fetch Interval</th>
            <th>Last Response</th>
            <th>Failures</th>
            <th>Last Update</th>
            <th>Next Update</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {feeds.map(feed => (
            <tr key={feed.id} className={feed.active ? '' : 'error'}>
              <td>
                <a href={feed.siteUri} target="_blank" rel="noopener noreferrer">
                  {feed.title || feed.feedUri}
                </a>
              </td>
              <td>{feed.active ? 'Active' : 'Paused'}</td>
              <td>
                <input
                  type="number"
                  min="300"
                  max={86400 * 30}
                  value={feed.fetchIntervalSeconds}
                  onChange={(e) => setFetchInterval(feed, Number(e.target.value))}
                  style={{ width: '6em' }}
                />
                <select
                  value=""
                  onChange={(e) => setFetchInterval(feed, Number(e.target.value))}
                >
                  <option value="" disabled>Presets</option>
                  {intervalOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  className="btn btn-primary"
                  onClick={() => saveFetchInterval(feed)}
                >
                  Save
                </button>
              </td>
              <td>{feed.lastResponseStatusCode ?? '-'}</td>
              <td>{feed.updateFailures}</td>
              <td>{formatTime(feed.refreshTime)}</td>
              <td>{formatTime(feed.nextUpdateTime)}</td>
              <td>
                <button
                  className={`btn ${feed.active ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => toggleActive(feed)}
                >
                  {feed.active ? 'Pause' : 'Activate'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
